import type { Scale } from '@tunasong/schemas'
import { displayAccidentals } from '../chord/concert-pitch.js'
import type { MelodyNoteRole } from './note-roles.js'

/**
 * The why line under the roll (melody toolkit plan M3; spec §5.3): one sentence about the selected note,
 * deterministic and offline. Beginner gets plain words; Intermediate and Advanced the same named form.
 *
 * Every sentence is in `whyLineCopy` and nowhere else: the wording is Bowie's proposal (plan appendix §3),
 * with the user, and a change to it is a change to that table.
 */

export type WhyLineLevel = 'beginner' | 'intermediate' | 'advanced'

interface Forms {
  named: string
  plain: string
}

/** Tokens: {note} {chord} {degree} {target} {from} {to} {key} {n} {a} {b} */
export const whyLineCopy = {
  chordTone: { named: '{note} over {chord}: the {degree}.', plain: '{note} over {chord}: a note from the chord.' },
  tension: {
    named: '{note} over {chord}: the {degree}, a tension.',
    plain: '{note} over {chord}: not in the chord, but it fits. It adds color.',
  },
  suspensionFalls: {
    named: '{note} over {chord}: the {degree}, a suspension. It falls to {target}.',
    plain: '{note} over {chord}: held over from the last chord. It settles on {target}.',
  },
  suspensionRises: {
    named: '{note} over {chord}: the {degree}, a suspension. It rises to {target}.',
    plain: '{note} over {chord}: held over from the last chord. It settles on {target}.',
  },
  leansFalls: {
    named: '{note} over {chord}: the {degree}, leaning on the beat. It falls to {target}.',
    plain: '{note} over {chord}: leans on the beat, then settles on {target}.',
  },
  leansRises: {
    named: '{note} over {chord}: the {degree}, leaning on the beat. It rises to {target}.',
    plain: '{note} over {chord}: leans on the beat, then settles on {target}.',
  },
  /** An avoid note always falls (Bowie): never "rise" */
  avoidStrong: {
    named: '{note} over {chord}: the {degree}, on a strong beat. It wants to fall to {target}.',
    plain: '{note} over {chord}: rubs against the chord. It wants to step down to {target}.',
  },
  avoidHeld: {
    named: '{note} over {chord}: the {degree}, held. It wants to fall to {target}.',
    plain: '{note} over {chord}: rubs against the chord. It wants to step down to {target}.',
  },
  passing: {
    named: '{note} over {chord}: a passing note from {from} to {to}.',
    plain: '{note} over {chord}: a stepping stone from {from} to {to}.',
  },
  neighborAround: {
    named: '{note} over {chord}: a neighbor note around {from}.',
    plain: '{note} over {chord}: a step away from {from} and back.',
  },
  neighborNext: {
    named: '{note} over {chord}: a neighbor note next to {from}.',
    plain: '{note} over {chord}: a step away from {from}.',
  },
  blue: {
    named: '{note} over {chord}: a blue note.',
    plain: '{note} over {chord}: a blue note. It gives a bluesy edge.',
  },
  outside: {
    named: '{note}: outside the key and the chord.',
    plain: '{note}: not in the key or the chord. It stands out.',
  },
  scaleDegree: { named: '{note}: the {degree} of {key}.', plain: '{note}: step {n} of the {key} scale.' },
  tonic: { named: '{note}: the tonic of {key}.', plain: '{note}: step {n} of the {key} scale.' },
  between: { named: '{note}: the {degree} of {key}.', plain: '{note}: between steps {a} and {b} of the {key} scale.' },
} satisfies Record<string, Forms>

const fill = (template: string, tokens: Record<string, string | number | undefined>) =>
  template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = tokens[name]
    return value === undefined ? match : String(value)
  })

const formsOf = (role: MelodyNoteRole): Forms => {
  const rises = role.target?.direction === 'rises'
  switch (role.role) {
    case 'chord-tone':
      return whyLineCopy.chordTone
    case 'tension':
      return whyLineCopy.tension
    case 'suspension':
      return rises ? whyLineCopy.suspensionRises : whyLineCopy.suspensionFalls
    case 'leans':
      return rises ? whyLineCopy.leansRises : whyLineCopy.leansFalls
    case 'avoid':
      return role.accent === 'held' ? whyLineCopy.avoidHeld : whyLineCopy.avoidStrong
    case 'passing':
      return whyLineCopy.passing
    case 'neighbor':
      return role.neighbors?.before !== undefined && role.neighbors.after !== undefined
        ? whyLineCopy.neighborAround
        : whyLineCopy.neighborNext
    case 'blue':
      return whyLineCopy.blue
    case 'outside':
      return whyLineCopy.outside
    default:
      return role.degree === '1'
        ? whyLineCopy.tonic
        : role.step === undefined
          ? whyLineCopy.between
          : whyLineCopy.scaleDegree
  }
}

/** "C major": a key is always named with its quality (Bowie) */
const keyName = (key: Scale | undefined) => (key ? `${displayAccidentals(key.root)} ${key.type ?? 'major'}` : undefined)

export interface WhyLineOptions {
  level: WhyLineLevel
  key?: Scale
}

export const whyLine = (role: MelodyNoteRole, options: WhyLineOptions): string => {
  const forms = formsOf(role)
  const template = options.level === 'beginner' ? forms.plain : forms.named
  const from = role.neighbors?.before ?? role.neighbors?.after
  return fill(template, {
    note: displayAccidentals(role.name),
    chord: role.chord === null ? undefined : displayAccidentals(role.chord),
    degree: role.degree,
    target: role.target ? displayAccidentals(role.target.name) : undefined,
    from: from === undefined ? undefined : displayAccidentals(from),
    to: role.neighbors?.after === undefined ? undefined : displayAccidentals(role.neighbors.after),
    key: keyName(options.key),
    n: role.step,
    a: role.between?.[0],
    b: role.between?.[1],
  })
}

/**
 * Spec §10: the role, as a note's accessible name carries it: "over G: the 4th, wants to fall to B". The
 * named forms of the why line, compacted; a proposal with the user like the rest.
 */
export const noteNameCopy = {
  chordTone: 'over {chord}: the {degree}',
  tension: 'over {chord}: the {degree}, a tension',
  suspension: 'over {chord}: the {degree}, a suspension, {direction} to {target}',
  leans: 'over {chord}: the {degree}, leaning on the beat, {direction} to {target}',
  avoid: 'over {chord}: the {degree}, wants to fall to {target}',
  passing: 'over {chord}: a passing note',
  neighbor: 'over {chord}: a neighbor note',
  blue: 'over {chord}: a blue note',
  outside: 'outside the key and the chord',
  scaleDegree: 'the {degree} of {key}',
  tonic: 'the tonic of {key}',
}

const nameTemplate = (role: MelodyNoteRole) => {
  switch (role.role) {
    case 'chord-tone':
      return noteNameCopy.chordTone
    case 'scale-degree':
      return role.degree === '1' ? noteNameCopy.tonic : noteNameCopy.scaleDegree
    default:
      return noteNameCopy[role.role]
  }
}

/** The role part of a note's accessible name */
export const roleDescription = (role: MelodyNoteRole, key?: Scale): string =>
  fill(nameTemplate(role), {
    chord: role.chord === null ? undefined : displayAccidentals(role.chord),
    degree: role.degree,
    target: role.target ? displayAccidentals(role.target.name) : undefined,
    direction: role.target?.direction,
    key: keyName(key),
  })
